import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { leadsAPI, leadActivitiesAPI, dealsAPI, hrAPI, voiceAPI, meetingSchedulerAPI, brainDocProcessorAPI, docRequirementsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowLeft, Mail, Phone, Video, MapPin, Edit, Trash2, Plus, FileText, Calendar, CheckCircle, MessageCircle, Paperclip, Download, X, DollarSign, Bot, Zap, Activity, Loader2, Link2, Copy, ExternalLink, PhoneCall, Building2, Globe, ChevronDown, MoreHorizontal, ClipboardList, Send, Sparkles, Lock, Unlock, Linkedin, Target, ClipboardCheck, Brain, FileCheck, Clock, AlertCircle, RefreshCw, Route, Rocket } from 'lucide-react';
import { LeadJourneyTimeline } from '../components/leads/LeadJourneyTimeline';
import { toast } from 'sonner';
import { handleError } from '../lib/errorHandler';
import { format, isToday, isYesterday } from 'date-fns';
import { parseUTC, formatLocal, formatLocalTime, formatLocalDate, getDateGroupLabel } from '../lib/dateUtils';
import EditLeadOffcanvas from '../components/EditLeadOffcanvas';
import CreateDealOffcanvas from '../components/CreateDealOffcanvas';
import ViewDealOffcanvas from '../components/ViewDealOffcanvas';
import EditDealOffcanvas from '../components/EditDealOffcanvas';
import SendEmailModal from '../components/SendEmailModal';
import SendWhatsAppModal from '../components/SendWhatsAppModal';
import LeadIntelligencePanel from '../components/LeadIntelligencePanel';
import AIDraftEmailSheet from '../components/AIDraftEmailSheet';
import AddNoteSheet from '../components/AddNoteSheet';
import BrainActivityTimeline from '../components/BrainActivityTimeline';
import { SmartCadencePanel } from '../components/SmartCadencePanel';
import { LinkedInOutreachPanel } from '../components/LinkedInOutreachPanel';
import ClosingStrategyPanel from '../components/ClosingStrategyPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  VisitDialog, MeetingSheet, MakeCallDialog, CallSummaryDialog,
  EditDealDialog, TaskSheet, FollowupSheet, EmailSheet, CallSheet
} from '../components/LeadActionDialogs';
import { VoiceCallButton, VoiceCallHistory, VoiceAIPanel } from '../components/VoiceAI';
import { BrainCallPanel } from '../components/BrainCallPanel';
import BrainMemoryPanel from '../components/BrainMemoryPanel';
import { CallLogCard } from '../components/CallLogCard';
import { ReengagementQueue } from '../components/ReengagementQueue';
import WhatsAppThreadView from '../components/WhatsAppThreadView';
import LinkedInThreadView from '../components/LinkedInThreadView';
import { useCurrency } from '../contexts/CurrencyContext';
import { usePermissions } from '../contexts/PermissionContext';
import { InvalidRoute, isValidId } from '../components/ui/page-guards';
import { useAsyncAction, SubmitButton, DeleteButton } from '../components/ui/action-button';
import LeadActivityContent from '../components/lead-details/LeadActivityContent';
import { LeadProfileSidebar } from '../components/lead-details/LeadProfileSidebar';

const statusConfigs = {
  new: { label: 'New', color: 'hsl(221 83% 53%)', bgColor: 'hsl(221 83% 96%)', textColor: 'hsl(221 83% 53%)' },
  contacted: { label: 'Contacted', color: 'hsl(45 93% 47%)', bgColor: 'hsl(45 93% 95%)', textColor: 'hsl(45 93% 35%)' },
  qualified: { label: 'Qualified', color: 'hsl(262 83% 58%)', bgColor: 'hsl(262 83% 97%)', textColor: 'hsl(262 83% 48%)' },
  converted: { label: 'Converted', color: 'hsl(142 76% 36%)', bgColor: 'hsl(142 76% 96%)', textColor: 'hsl(142 76% 30%)' },
  lost: { label: 'Lost', color: 'hsl(0 84% 60%)', bgColor: 'hsl(0 84% 97%)', textColor: 'hsl(0 84% 45%)' }
};

export default function LeadDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currency = useCurrency();
  const { hasPermission } = usePermissions();
  
  // Route guard - validate ID before any operations
  if (!isValidId(id)) {
    return (
      <InvalidRoute
        title="Invalid Lead"
        message="The lead ID is missing or invalid. Please select a valid lead from the list."
        backPath="/leads"
        backLabel="Back to Leads"
      />
    );
  }
  
  const [lead, setLead] = useState(null);
  const [launchingCommercial, setLaunchingCommercial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activity');
  const [activityTab, setActivityTab] = useState('all');
  const [intelligenceSubTab, setIntelligenceSubTab] = useState('overview');
  const [highlightedCallId, setHighlightedCallId] = useState(null);
  
  // Activity states
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [emails, setEmails] = useState([]);
  const [visits, setVisits] = useState([]);
  const [calls, setCalls] = useState([]);
  const [brainCalls, setBrainCalls] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [scheduledMeetings, setScheduledMeetings] = useState([]);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [linkedinMessages, setLinkedinMessages] = useState([]);
  const [waCount, setWaCount] = useState(0);
  const [liCount, setLiCount] = useState(0);
  const [deals, setDeals] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [docVerification, setDocVerification] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docFollowupLoading, setDocFollowupLoading] = useState(false);
  
  const [noteComments, setNoteComments] = useState({});
  const [commentText, setCommentText] = useState({});

  // New modal/offcanvas states
  const [editLeadOpen, setEditLeadOpen] = useState(false);
  const [createDealOpen, setCreateDealOpen] = useState(false);
  const [viewDealOpen, setViewDealOpen] = useState(false);
  const [editDealOpen, setEditDealOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [sendWhatsAppOpen, setSendWhatsAppOpen] = useState(false);
  const [aiDraftEmailOpen, setAiDraftEmailOpen] = useState(false);
  const [aiDraftAction, setAiDraftAction] = useState({ type: '', label: '' });
  const [selectedDeal, setSelectedDeal] = useState(null);
  
  // Form states
  const [noteText, setNoteText] = useState('');
  const [taskForm, setTaskForm] = useState({ title: '', description: '', dueDate: '', priority: 'medium' });
  const [followupForm, setFollowupForm] = useState({ date: '', notes: '' });
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [visitForm, setVisitForm] = useState({ date: '', time: '', location: '', purpose: '' });
  const [meetingForm, setMeetingForm] = useState({ date: '', time: '', duration: '', agenda: '', platform: '', meeting_link: '' });
  const [callForm, setCallForm] = useState({ duration: '', notes: '', summary: '', call_type: 'outgoing' });
  
  // Dialog states for quick actions
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [callSummaryDialogOpen, setCallSummaryDialogOpen] = useState(false);
  const [makeCallDialogOpen, setMakeCallDialogOpen] = useState(false);
  const [editDealDialogOpen, setEditDealDialogOpen] = useState(false);
  // AI Actions state
  const [aiStatus, setAiStatus] = useState(null);
  const [aiActivities, setAiActivities] = useState([]);
  const [reanalyzing, setReanalyzing] = useState(false);
  // Reveal state
  const [revealing, setRevealing] = useState(false);
  // Brain sidebar refresh counter
  const [brainRefreshKey, setBrainRefreshKey] = useState(0);
  const triggerBrainRefresh = () => setBrainRefreshKey(k => k + 1);
  
  // Reschedule dialog state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // { id, type: 'followup' }
  const [rescheduleDate, setRescheduleDate] = useState('');

  const [dealForm, setDealForm] = useState({
    title: '',
    amount: 0,
    expectedCloseDate: '',
    stage: 'negotiation',
    description: '',
    lineItems: [],
    inventoryItems: []
  });

  useEffect(() => {
    loadLead();
    loadActivities();
    loadTeamMembers();
  }, [id]);

  // Handle URL params for deep linking (e.g., ?tab=calls&highlight=<callId>)
  useEffect(() => {
    const tab = searchParams.get('tab');
    const highlight = searchParams.get('highlight');
    
    if (tab === 'calls') {
      setActiveTab('activity');
      setActivityTab('calls');
      if (highlight) {
        setHighlightedCallId(highlight);
        // Clear URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('tab');
        newParams.delete('highlight');
        setSearchParams(newParams, { replace: true });
        // Scroll to highlighted call after render
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="call-record-${highlight}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          // Remove highlight after 5 seconds
          setTimeout(() => setHighlightedCallId(null), 5000);
        }, 500);
      }
    }
  }, [searchParams, setSearchParams]);

  const loadTeamMembers = async () => {
    try {
      const response = await hrAPI.getTeamMembers();
      setTeamMembers(response.data || []);
    } catch (error) {
      handleError('LeadDetails', error, { silent: true });
    }
  };

  // Helper function to get user name from ID
  const getUserName = useCallback((userId) => {
    // First try to find in teamMembers
    const member = teamMembers.find(m => m.id === userId);
    return member ? member.name : 'User';
  }, [teamMembers]);
  
  // Helper to get creator name from item (prefer API response field)
  const getCreatorName = useCallback((item) => {
    if (item.created_by === 'ai-brain' || item.ai_generated) return 'AI Brain';
    if (item.created_by_name) return item.created_by_name;
    return getUserName(item.created_by);
  }, [getUserName]);

  // Iter 59b — Defer heavy AI calls (LLM-backed) until the Intelligence tab opens.
  // Previously these fired on mount and could block lead detail for 30-60s
  // during worker-loop contention. Now they fire only when the user looks at them.
  useEffect(() => {
    if (!lead?.id) return;
    if (activeTab !== 'intelligence' && activeTab !== 'ai' && activeTab !== 'activity') return;
    loadAIStatus(lead.id);
    loadAIActivities(lead.id);
  }, [lead?.id, activeTab]);

  const loadLead = async () => {
    try {
      const response = await leadsAPI.getOne(id);
      setLead(response.data);
    } catch (error) {
      handleError('LeadDetails', error, { fallbackMessage: 'Failed to load lead details' });
      toast.error(`Failed to load lead details: ${error.response?.data?.detail || error.message}`);
      // Don't redirect immediately - show error state instead
      setLead(null);
    } finally {
      setLoading(false);
    }
  };

  // Realtime: refresh this lead when Apollo webhook (or any backend actor)
  // broadcasts a lead_updated event for this lead_id. Fixes the "Not
  // available" ghost state where the DB has the phone but the page still
  // shows unrevealed because it loaded before the async webhook landed.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let socket = null;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      if (!token) return; // no auth → nothing to open
      const wsUrl = `${base.replace(/^http/, 'ws')}/api/ws/connect?token=${encodeURIComponent(token)}&channels=sales`;
      socket = new WebSocket(wsUrl);
      socket.onmessage = (evt) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(evt.data);
          // Backend broadcast shape: {event, data:{lead_id, changes, source}, ...}
          const eventName = msg?.event || msg?.type;
          const payload = msg?.data || msg;
          if (eventName === 'lead_updated' && payload?.lead_id === id) {
            if (payload.changes && typeof payload.changes === 'object') {
              setLead((prev) => (prev ? { ...prev, ...payload.changes } : prev));
            }
            loadLead();
            const src = payload.source ? ` (via ${payload.source})` : '';
            if (payload?.changes?.phone) {
              try { toast.success(`Phone number just arrived${src}`); } catch (_) { /* noop */ }
            }
          }
        } catch (_) { /* non-fatal */ }
      };
    } catch (_) { /* ws not available — page still works via next manual refresh */ }
    return () => { cancelled = true; try { socket?.close(); } catch (_) {} };
  }, [id]);

  const loadActivities = async () => {
    try {
      const safe = (promise, fallback = { data: [] }) => promise.catch(() => fallback);
      const [notesRes, tasksRes, followupsRes, emailsRes, visitsRes, callsRes, meetingsRes, whatsappRes, dealsRes, attachmentsRes, brainDraftsRes, schedulerMeetingsRes, brainCallsRes, linkedinRes] = await Promise.all([
        safe(leadActivitiesAPI.getNotes(id)),
        safe(leadActivitiesAPI.getTasks(id)),
        safe(leadActivitiesAPI.getFollowUps(id)),
        safe(leadActivitiesAPI.getEmails(id)),
        safe(leadActivitiesAPI.getVisits(id)),
        safe(leadActivitiesAPI.getCalls(id)),
        safe(leadActivitiesAPI.getMeetings(id)),
        safe(leadActivitiesAPI.getWhatsAppActivities(id)),
        safe(dealsAPI.getAll({ lead_id: id }), { data: { data: [] } }),
        safe(leadActivitiesAPI.getAttachments(id)),
        safe(api.get(`/api/brain/email-drafts?lead_id=${id}`), { data: { drafts: [] } }),
        safe(meetingSchedulerAPI.getLeadMeetings(id), { data: { meetings: [], lead_meetings: [] } }),
        safe(voiceAPI.getBrainCallsForLead(id), { data: { calls: [] } }),
        safe(api.get(`/api/linkedin-tasks?lead_id=${id}`), { data: { tasks: [] } }),
      ]);
      
      setNotes(notesRes.data || []);
      setTasks(tasksRes.data || []);
      setFollowups(followupsRes.data || []);
      // Merge brain email drafts into emails list
      const regularEmails = emailsRes.data || [];
      const brainDrafts = (brainDraftsRes.data?.drafts || []).map(d => ({
        ...d,
        ai_generated: true,
        subject: d.subject || 'AI Draft',
        body: d.body || '',
        created_by: 'ai-brain',
      }));
      setEmails([...regularEmails, ...brainDrafts].sort((a, b) => 
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      ));
      setVisits(visitsRes.data || []);
      setCalls(callsRes.data || []);
      setBrainCalls(brainCallsRes.data?.calls || []);
      // Dedup: lead_meetings rows that mirror a scheduled_meetings row (have
      // scheduled_meeting_id) are shown via `scheduledMeetings` below — drop them
      // here so a booked/auto-scheduled meeting isn't counted twice on the tab.
      setMeetings((meetingsRes.data || []).filter(m => !m.scheduled_meeting_id));
      setScheduledMeetings(schedulerMeetingsRes.data?.meetings || schedulerMeetingsRes.data?.lead_meetings || []);
      setWhatsappMessages(whatsappRes.data || []);
      setLinkedinMessages(linkedinRes.data?.tasks || []);
      // Badge counts sourced from the SAME conversation endpoints the thread
      // views use (tenant-scoped, includes AI/automation/teammate messages) so
      // the tab count matches the real thread instead of the old user-scoped
      // lead_activities/tasks-only query that showed (0). Never lower than the
      // legacy count, so nothing regresses.
      try {
        const [waConv, liConv] = await Promise.all([
          api.get(`/api/whatsapp/conversation/${id}`).catch(() => null),
          api.get(`/api/linkedin/conversation/${id}`).catch(() => null),
        ]);
        const wa = waConv?.data || {};
        const waTotal = (wa.outbound_count || 0) + (wa.inbound_count || 0);
        setWaCount(Math.max(waTotal, (whatsappRes.data || []).length));
        const li = liConv?.data || {};
        const liTotal = li.total != null ? li.total : ((li.outbound_count || 0) + (li.inbound_count || 0));
        setLiCount(Math.max(liTotal, (linkedinRes.data?.tasks || []).length));
      } catch (_e) { /* keep legacy fallback counts */ }
      setDeals(dealsRes.data?.data || []);
      setAttachments(attachmentsRes.data || []);
      // Iter 59b: doc verification is an LLM-backed summary — defer to docs tab
      // to avoid blocking lead detail first-paint. It auto-loads when user
      // opens the verification tab (hooked in the tab change effect below).
    } catch (error) {
      handleError('LeadDetails', error, { silent: true });
      toast.error('Failed to load activities');
    }
  };

  const loadNoteComments = async (noteId) => {
    try {
      const response = await leadActivitiesAPI.getNoteComments(noteId);
      setNoteComments(prev => ({ ...prev, [noteId]: response.data || [] }));
    } catch (error) {
      handleError('LeadDetails', error, { silent: true });
    }
  };

  // Iter 59b — lazy-load doc verification summary when user opens verification tab.
  useEffect(() => {
    if (!lead?.id || docVerification) return;
    if (activeTab !== 'verification' && activeTab !== 'documents') return;
    setDocLoading(true);
    brainDocProcessorAPI.getVerificationSummary(id)
      .then(r => setDocVerification(r.data))
      .catch(() => {})
      .finally(() => setDocLoading(false));
  }, [lead?.id, activeTab, docVerification, id]);

  const handleAddComment = async (noteId) => {
    const text = commentText[noteId]?.trim();
    if (!text) return;
    try {
      const response = await leadActivitiesAPI.addNoteComment(noteId, text);
      setNoteComments(prev => ({
        ...prev,
        [noteId]: [...(prev[noteId] || []), response.data]
      }));
      setCommentText(prev => ({ ...prev, [noteId]: '' }));
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  // Load comments when notes change
  useEffect(() => {
    notes.forEach(note => {
      if (!noteComments[note.id]) {
        loadNoteComments(note.id);
      }
    });
  }, [notes]);

  const [executeAddNote, addingNote] = useAsyncAction(async (text) => {
    if (!text?.trim()) throw new Error('Note cannot be empty');
    const response = await leadActivitiesAPI.createNote({
      lead_id: id,
      text: text
    });
    setNotes([response.data, ...notes]);
    setNoteText('');
    setNoteDialogOpen(false);
    triggerBrainRefresh();
  }, 'Note added', 'Failed to add note');

  const handleAddNote = async (htmlContent, _attachments) => {
    const text = htmlContent || noteText;
    if (!text.trim()) return;
    await executeAddNote(text);
  };

  const [executeAddTask, addingTask] = useAsyncAction(async () => {
    if (!taskForm.title.trim()) throw new Error('Task title cannot be empty');
    const response = await leadActivitiesAPI.createTask({
      lead_id: id,
      title: taskForm.title,
      due_date: taskForm.dueDate || null
    });
    setTasks([response.data, ...tasks]);
    setTaskForm({ title: '', description: '', dueDate: '', priority: 'medium' });
    triggerBrainRefresh();
  }, 'Task added', 'Failed to add task');

  const handleAddTask = async () => {
    if (!taskForm.title.trim()) return;
    await executeAddTask();
  };

  const [executeAddFollowup, addingFollowup] = useAsyncAction(async () => {
    if (!followupForm.date) throw new Error('Date is required');
    const response = await leadActivitiesAPI.createFollowUp({
      lead_id: id,
      date: followupForm.date,
      type: 'general',
      notes: followupForm.notes
    });
    setFollowups([response.data, ...followups]);
    setFollowupForm({ date: '', notes: '' });
    triggerBrainRefresh();
  }, 'Follow-up added', 'Failed to add follow-up');

  const handleAddFollowup = async () => {
    if (!followupForm.date) return;
    await executeAddFollowup();
  };

  const [executeSendEmail, sendingEmail] = useAsyncAction(async () => {
    if (!emailForm.subject.trim()) throw new Error('Subject is required');
    const response = await leadActivitiesAPI.createEmail({
      lead_id: id,
      subject: emailForm.subject,
      body: emailForm.body
    });
    setEmails([response.data, ...emails]);
    setEmailForm({ subject: '', body: '' });
    triggerBrainRefresh();
  }, 'Email activity recorded', 'Failed to record email');

  const handleSendEmail = async () => {
    if (!emailForm.subject.trim()) return;
    await executeSendEmail();
  };

  const [executeAddVisit, addingVisit] = useAsyncAction(async () => {
    if (!visitForm.date) throw new Error('Date is required');
    const response = await leadActivitiesAPI.createVisit({
      lead_id: id,
      date: visitForm.date,
      time: visitForm.time || null,
      location: visitForm.location,
      purpose: visitForm.purpose,
      status: 'scheduled'
    });
    setVisits([response.data, ...visits]);
    setVisitForm({ date: '', time: '', location: '', purpose: '' });
    setVisitDialogOpen(false);
    triggerBrainRefresh();
  }, 'Visit scheduled', 'Failed to schedule visit');

  const handleAddVisit = async () => {
    if (!visitForm.date) return;
    await executeAddVisit();
  };

  const [executeAddMeeting, addingMeeting] = useAsyncAction(async () => {
    if (!meetingForm.date) throw new Error('Date is required');
    const response = await leadActivitiesAPI.createMeeting({
      lead_id: id,
      date: meetingForm.date,
      time: meetingForm.time,
      duration: meetingForm.duration,
      agenda: meetingForm.agenda,
      location: meetingForm.platform === 'face_to_face' ? 'In Person' : 'Virtual',
      platform: meetingForm.platform || null,
      meeting_link: meetingForm.meeting_link || null,
      status: 'scheduled'
    });
    setMeetings([response.data, ...meetings]);
    setMeetingForm({ date: '', time: '', duration: '', agenda: '', platform: '', meeting_link: '' });
    setMeetingDialogOpen(false);
    triggerBrainRefresh();
  }, 'Meeting scheduled', 'Failed to schedule meeting');

  const handleAddMeeting = async () => {
    if (!meetingForm.date) return;
    await executeAddMeeting();
  };

  const handleQuickNote = () => {
    setNoteDialogOpen(true);
    setActivityTab('notes');
  };

  const handleRevealContact = async () => {
    if (revealing || !lead) return;
    setRevealing(true);
    try {
      const response = await leadsAPI.revealContact(lead.id);
      const data = response.data;
      if (data.success && data.revealed) {
        const details = [data.email, data.phone].filter(Boolean).join(' | ');
        toast.success('Contact revealed', { description: details || 'Contact details unlocked' });
        await loadLead();
      } else if (data.success && !data.revealed) {
        toast.warning('No contact info found', {
          description: data.message || 'No email or phone available. No credits charged.',
        });
        await loadLead();
      } else {
        toast.error(data.message || 'Reveal failed');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to reveal contact';
      if (error.response?.status === 402) {
        toast.error('Insufficient credits', { description: 'Purchase more reveal credits.' });
      } else {
        toast.error(msg);
      }
    } finally {
      setRevealing(false);
    }
  };

  const handleQuickEmail = () => {
    if (lead && lead.email) {
      setSendEmailOpen(true);
    } else {
      toast.error('No email address available');
    }
  };

  const handleQuickCall = () => {
    if (lead && lead.phone) {
      window.location.href = `tel:${lead.phone}`;
    } else {
      toast.error('No phone number available');
    }
  };

  const handleQuickWhatsApp = () => {
    if (lead && lead.phone) {
      setSendWhatsAppOpen(true);
    } else {
      toast.error('No phone number available');
    }
  };

  const handleAICall = async () => {
    if (!lead?.phone) {
      toast.error('No phone number available');
      return;
    }
    toast.loading(`AI calling ${lead.name}...`, { id: 'ai-call-detail' });
    try {
      const res = await voiceAPI.initiateBrainCall(lead.id);
      toast.success(`AI call initiated to ${lead.name}`, { 
        id: 'ai-call-detail', 
        description: `${res.data.call_type} call — ${res.data.mode} mode`, 
        duration: 5000 
      });
      // Refresh calls list to show the new call record
      setTimeout(async () => {
        try {
          const [callsRes, brainCallsRes] = await Promise.all([
            leadActivitiesAPI.getCalls(lead.id),
            voiceAPI.getBrainCallsForLead(lead.id).catch(() => ({ data: { calls: [] } })),
          ]);
          setCalls(Array.isArray(callsRes.data) ? callsRes.data : []);
          setBrainCalls(brainCallsRes.data?.calls || []);
        } catch {}
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate AI call', { id: 'ai-call-detail' });
    }
  };

  // Handlers for off-canvas sheets
  const handleCreateTask = async () => {
    await handleAddTask();
    setTaskDialogOpen(false);
  };

  const handleCreateFollowup = async () => {
    await handleAddFollowup();
    setFollowupDialogOpen(false);
  };

  const handleCreateEmail = async () => {
    await handleSendEmail();
    setEmailDialogOpen(false);
  };

  const handleCreateCall = async () => {
    if (!callForm.duration && !callForm.notes && !callForm.summary) {
      toast.error('Please fill at least one field');
      return;
    }
    try {
      const response = await leadActivitiesAPI.createCall({
        lead_id: id,
        duration: callForm.duration || null,
        notes: callForm.notes,
        summary: callForm.summary,
        call_type: callForm.call_type
      });
      setCalls([response.data, ...calls]);
      setCallForm({ duration: '', notes: '', summary: '', call_type: 'outgoing' });
      setCallDialogOpen(false);
      toast.success('Call logged successfully');
      triggerBrainRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log call');
    }
  };

  // New handler functions
  const handleEditLead = () => {
    setEditLeadOpen(true);
  };

  const handleDeleteLead = async () => {
    if (!window.confirm(`Delete lead "${lead?.name || 'this lead'}"? This cannot be undone.`)) return;
    try {
      await leadsAPI.delete(id);
      toast.success('Lead deleted');
      navigate('/leads');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      await api.post(`/api/leads/${id}/intelligence`);
      toast.success('Brain re-analysis complete');
      const leadRes = await leadsAPI.getById(id);
      setLead(leadRes.data);
    } catch {
      toast.error('Failed to re-analyze lead');
    } finally {
      setReanalyzing(false);
    }
  };

  const handleCreateDeal = () => {
    setCreateDealOpen(true);
  };

  const handleViewDeal = (deal) => {
    setSelectedDeal(deal);
    setViewDealOpen(true);
  };

  const handleEditDeal = (deal) => {
    setSelectedDeal(deal);
    setEditDealOpen(true);
    setViewDealOpen(false);
  };

  const handleDealSuccess = () => {
    loadActivities();
  };

  const handleDealDeleted = () => {
    loadActivities();
  };

  const handleDealConvertedToQuote = (data) => {
    loadLead();
    loadActivities();
    window.open(`/quotes/${data.quote_id}`, '_blank');
  };

  const handleQuickVisit = () => {
    setVisitDialogOpen(true);
  };

  const handleQuickMeeting = () => {
    setMeetingDialogOpen(true);
  };

  const [bookingLink, setBookingLink] = useState('');
  const [loadingBookingLink, setLoadingBookingLink] = useState(false);

  const handleGetBookingLink = async () => {
    setLoadingBookingLink(true);
    try {
      const res = await meetingSchedulerAPI.getLeadBookingLink(id);
      const link = res?.data?.booking_link;
      if (link) {
        setBookingLink(link);
      } else {
        toast.error('Connect Google Calendar in Integrations to generate a booking link');
      }
    } catch (e) {
      toast.error('Could not get booking link', { description: 'Connect Google Calendar in Integrations first.' });
    } finally {
      setLoadingBookingLink(false);
    }
  };

  const handleCopyBookingLink = (link) => {
    navigator.clipboard?.writeText(link);
    toast.success('Booking link copied');
  };

  const handleInsertBookingIntoEmail = (link) => {
    setEmailForm((prev) => ({
      ...prev,
      subject: prev.subject || "Let's schedule a meeting",
      body: (prev.body ? prev.body + '\n\n' : `Hi ${lead?.name || ''},\n\n`) + `Book a time that works for you here: ${link}`,
    }));
    setMeetingDialogOpen(false);
    setEmailDialogOpen(true);
  };

  const handleSendBookingWhatsApp = (link) => {
    const phone = (lead?.phone || '').replace(/[^0-9]/g, '');
    const text = encodeURIComponent(`Hi ${lead?.name || ''}, book a time for our meeting: ${link}`);
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const [executeSaveCall, savingCall] = useAsyncAction(async () => {
    if (!callForm.notes && !callForm.summary) {
      throw new Error('Please add notes or summary');
    }
    const response = await leadActivitiesAPI.createCall({
      lead_id: id,
      duration: callForm.duration,
      notes: callForm.notes,
      summary: callForm.summary,
      call_type: callForm.call_type
    });
    setCalls([response.data, ...calls]);
    setCallForm({ duration: '', notes: '', summary: '', call_type: 'outgoing' });
    setCallSummaryDialogOpen(false);
    setMakeCallDialogOpen(false);
  }, 'Call logged', 'Failed to log call');

  const handleSaveCall = async () => {
    if (!callForm.notes && !callForm.summary) {
      toast.error('Please add notes or summary');
      return;
    }
    await executeSaveCall();
  };

  const [executeUploadFile, uploadingFile] = useAsyncAction(async (file) => {
    const response = await leadActivitiesAPI.uploadAttachment(id, file);
    setAttachments([response.data, ...attachments]);
  }, 'File uploaded successfully', 'Failed to upload file');

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not supported. Please upload images, PDF, TXT, DOC, or Excel files.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    await executeUploadFile(file);
    // Reset file input
    event.target.value = '';
  };

  const [executeDeleteAttachment, deletingAttachment] = useAsyncAction(async (attachmentId) => {
    await leadActivitiesAPI.deleteAttachment(attachmentId);
    setAttachments(attachments.filter(a => a.id !== attachmentId));
    triggerBrainRefresh();
  }, 'Attachment deleted', 'Failed to delete attachment');

  const handleDeleteAttachment = async (attachmentId) => {
    await executeDeleteAttachment(attachmentId);
  };

  // AI Actions handlers
  const loadAIStatus = async (leadId) => {
    try {
      const response = await leadsAPI.getAIStatus(leadId);
      setAiStatus(response.data);
    } catch (error) {
      handleError('LeadDetails', error, { silent: true });
      setAiStatus(null);
    }
  };

  const loadAIActivities = async (leadId) => {
    try {
      const response = await leadsAPI.getAIActivities(leadId);
      setAiActivities(response.data.ai_activities || []);
    } catch (error) {
      handleError('LeadDetails', error, { silent: true });
      setAiActivities([]);
    }
  };

  const [executeRunSalesAI, aiLoading] = useAsyncAction(async () => {
    if (!lead?.id) return;
    
    const response = await leadsAPI.runSalesAI(lead.id, { action_type: 'full_playbook' });
    const result = response.data;
    
    if (result.success) {
      const modeLabel = result.mode === 'LIVE' ? 'LIVE' : 'SIMULATED';
      const actionsCount = result.actions_executed?.length || 0;
      toast.success(`Sales AI executed (${modeLabel}) — ${actionsCount} action${actionsCount !== 1 ? 's' : ''} sent`, {
        description: result.mode === 'LIVE' ? '🟢 Actions were executed in LIVE mode' : '🟡 Actions were simulated only'
      });
      // Reload AI activities to show the new execution
      await loadAIActivities(lead.id);
      await loadAIStatus(lead.id);
    } else {
      throw new Error(result.blocked_reason || 'Sales AI Blocked');
    }
  }, null, 'Failed to execute Sales AI');

  const handleRunSalesAI = async () => {
    await executeRunSalesAI();
  };

  // Mock inventory items
  const inventoryItems = [
    { id: 1, name: 'CRM Software License', price: 299, stock: 100 },
    { id: 2, name: 'Mobile App Integration', price: 499, stock: 50 },
    { id: 3, name: 'Premium Support Package', price: 999, stock: 25 },
    { id: 4, name: 'Training Session (per user)', price: 149, stock: 500 },
    { id: 5, name: 'Custom Integration', price: 2499, stock: 10 }
  ];

  const handleAddLineItem = () => {
    const newLineItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      price: 0,
      tax: 0,
      total: 0
    };
    setDealForm({
      ...dealForm,
      lineItems: [...dealForm.lineItems, newLineItem]
    });
  };

  const handleRemoveLineItem = (itemId) => {
    setDealForm({
      ...dealForm,
      lineItems: dealForm.lineItems.filter(item => item.id !== itemId)
    });
  };

  const handleUpdateLineItem = (itemId, field, value) => {
    const updatedItems = dealForm.lineItems.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        // Recalculate total
        const subtotal = (updated.quantity || 0) * (updated.price || 0);
        updated.total = subtotal + (updated.tax || 0);
        return updated;
      }
      return item;
    });
    setDealForm({ ...dealForm, lineItems: updatedItems });
  };

  const handleAddInventoryItem = () => {
    const newItem = {
      id: Date.now(),
      itemId: '',
      itemName: '',
      quantity: 1,
      price: 0,
      tax: 0,
      taxRate: 0,
      total: 0
    };
    setDealForm({
      ...dealForm,
      inventoryItems: [...dealForm.inventoryItems, newItem]
    });
  };

  const handleRemoveInventoryItem = (itemId) => {
    setDealForm({
      ...dealForm,
      inventoryItems: dealForm.inventoryItems.filter(item => item.id !== itemId)
    });
  };

  const handleUpdateInventoryItem = (itemId, field, value) => {
    const updatedItems = dealForm.inventoryItems.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        
        // If item selection changed, update price and name
        if (field === 'itemId') {
          const selectedItem = inventoryItems.find(i => i.id === parseInt(value));
          if (selectedItem) {
            updated.itemName = selectedItem.name;
            updated.price = selectedItem.price;
          }
        }
        
        // Recalculate total
        const subtotal = (updated.quantity || 0) * (updated.price || 0);
        const taxAmount = subtotal * ((updated.taxRate || 0) / 100);
        updated.tax = taxAmount;
        updated.total = subtotal + taxAmount;
        return updated;
      }
      return item;
    });
    setDealForm({ ...dealForm, inventoryItems: updatedItems });
  };

  const calculateDealTotal = () => {
    const lineItemsTotal = dealForm.lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const inventoryTotal = dealForm.inventoryItems.reduce((sum, item) => sum + (item.total || 0), 0);
    return lineItemsTotal + inventoryTotal;
  };

  const [executeSaveDeal, savingDeal] = useAsyncAction(async () => {
    if (!dealForm.title.trim()) {
      throw new Error('Please enter deal title');
    }
    
    // Build line items from the form or default to lead value
    const formLineItems = dealForm.lineItems.filter(item => item.description && item.description.trim());
    const formInventoryItems = dealForm.inventoryItems.filter(item => item.itemId);
    
    const lineItems = formLineItems.length > 0
      ? formLineItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          price: parseFloat(item.price) || 0,
          tax_rate: parseFloat(item.taxRate) || 0,
        }))
      : [{
          description: dealForm.title || `Deal for ${lead?.name || 'Lead'}`,
          quantity: 1,
          price: parseFloat(dealForm.amount) || parseFloat(lead?.value) || 1000,
          tax_rate: 0,
        }];
    
    const response = await dealsAPI.create({
      lead_id: id,
      deal_name: dealForm.title,
      expected_close_date: dealForm.expectedCloseDate || undefined,
      stage: dealForm.stage || 'Proposal',
      description: dealForm.description || '',
      line_items: lineItems,
      items: formInventoryItems.map(item => ({
        item_id: item.itemId,
        item_name: item.itemName || '',
        quantity: parseFloat(item.quantity) || 1,
        unit_price: parseFloat(item.unitPrice) || 0,
        tax_rate: parseFloat(item.taxRate) || 0,
      })),
    });
    setDeals([response.data, ...deals]);
    setDealForm({
      title: '',
      amount: 0,
      expectedCloseDate: '',
      stage: 'negotiation',
      description: '',
      lineItems: [],
      inventoryItems: []
    });
    triggerBrainRefresh();
  }, 'Deal created successfully', 'Failed to create deal');

  const handleSaveDeal = async () => {
    if (!dealForm.title.trim()) {
      toast.error('Please enter deal title');
      return;
    }
    await executeSaveDeal();
  };

  const [executeToggleTask, togglingTask] = useAsyncAction(async (taskId, newCompleted) => {
    await leadActivitiesAPI.toggleTask(taskId, newCompleted);
    setTasks(tasks.map(t => t.id === taskId ? { ...t, completed: newCompleted } : t));
  }, null, 'Failed to update task');

  const handleToggleTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const newCompleted = !task.completed;
    await executeToggleTask(taskId, newCompleted);
  };

  const [executeTaskStatusChange, changingTaskStatus] = useAsyncAction(async (taskId, status) => {
    await leadActivitiesAPI.updateTaskStatus(taskId, status);
    setTasks(tasks.map(t => t.id === taskId ? { ...t, status, completed: status === 'completed' } : t));
  }, 'Task status updated', 'Failed to update task status');

  const handleTaskStatusChange = async (taskId, status) => {
    await executeTaskStatusChange(taskId, status);
  };

  const [executeFollowUpStatusChange, changingFollowUpStatus] = useAsyncAction(async (followupId, status) => {
    await leadActivitiesAPI.updateFollowUpStatus(followupId, status);
    setFollowups(followups.map(f => f.id === followupId ? { ...f, status } : f));
  }, 'Follow-up status updated', 'Failed to update follow-up status');

  const handleFollowUpStatusChange = async (followupId, status) => {
    await executeFollowUpStatusChange(followupId, status);
  };

  const [executeAddFollowUpSummary, addingFollowUpSummary] = useAsyncAction(async (followupId, summary) => {
    const followup = followups.find(f => f.id === followupId);
    await leadActivitiesAPI.updateFollowUpStatus(followupId, followup.status, summary);
    setFollowups(followups.map(f => f.id === followupId ? { ...f, summary } : f));
  }, 'Summary added', 'Failed to add summary');

  const handleAddFollowUpSummary = async (followupId, summary) => {
    if (!summary.trim()) return;
    await executeAddFollowUpSummary(followupId, summary);
  };

  const [executeRescheduleFollowUp, reschedulingFollowUp] = useAsyncAction(async (followupId, newDate) => {
    await leadActivitiesAPI.rescheduleFollowUp(followupId, newDate);
    setFollowups(followups.map(f => f.id === followupId ? { ...f, date: newDate, status: 'pending' } : f));
  }, 'Follow-up rescheduled', 'Failed to reschedule follow-up');

  const handleRescheduleFollowUp = async (followupId) => {
    setRescheduleTarget({ id: followupId, type: 'followup' });
    setRescheduleDate('');
    setRescheduleDialogOpen(true);
  };

  const confirmReschedule = async () => {
    if (!rescheduleDate || !rescheduleTarget) return;
    if (rescheduleTarget.type === 'followup') {
      await executeRescheduleFollowUp(rescheduleTarget.id, rescheduleDate);
    }
    setRescheduleDialogOpen(false);
    setRescheduleTarget(null);
    setRescheduleDate('');
  };

  const [executeDeleteFollowUp, deletingFollowUp] = useAsyncAction(async (followupId) => {
    await leadActivitiesAPI.deleteFollowUp(followupId);
    setFollowups(followups.filter(f => f.id !== followupId));
    triggerBrainRefresh();
  }, 'Follow-up deleted', 'Failed to delete follow-up');

  const handleDeleteFollowUp = async (followupId) => {
    await executeDeleteFollowUp(followupId);
  };

  const [executeAddEmailReply, addingEmailReply] = useAsyncAction(async (emailId, replyText) => {
    await leadActivitiesAPI.addEmailReply(emailId, replyText);
    setEmails(emails.map(e => e.id === emailId ? { ...e, reply: replyText, has_reply: true } : e));
  }, 'Reply added', 'Failed to add reply');

  const handleAddEmailReply = async (emailId, replyText) => {
    if (!replyText.trim()) return;
    await executeAddEmailReply(emailId, replyText);
  };

  const [executeDeleteEmail, deletingEmail] = useAsyncAction(async (emailId) => {
    await leadActivitiesAPI.deleteEmail(emailId);
    setEmails(emails.filter(e => e.id !== emailId));
    triggerBrainRefresh();
  }, 'Email deleted', 'Failed to delete email');

  const handleDeleteEmail = async (emailId) => {
    await executeDeleteEmail(emailId);
  };

  const [executeVisitStatusChange, changingVisitStatus] = useAsyncAction(async (visitId, status) => {
    await leadActivitiesAPI.updateVisitStatus(visitId, status);
    setVisits(visits.map(v => v.id === visitId ? { ...v, status } : v));
  }, 'Visit status updated', 'Failed to update visit status');

  const handleVisitStatusChange = async (visitId, status) => {
    await executeVisitStatusChange(visitId, status);
  };

  const [executeAddVisitSummary, addingVisitSummary] = useAsyncAction(async (visitId, summary) => {
    const visit = visits.find(v => v.id === visitId);
    await leadActivitiesAPI.updateVisitStatus(visitId, visit.status, summary);
    setVisits(visits.map(v => v.id === visitId ? { ...v, summary } : v));
  }, 'Summary added', 'Failed to add summary');

  const handleAddVisitSummary = async (visitId, summary) => {
    if (!summary.trim()) return;
    await executeAddVisitSummary(visitId, summary);
  };

  const [executeDeleteVisit, deletingVisit] = useAsyncAction(async (visitId) => {
    await leadActivitiesAPI.deleteVisit(visitId);
    setVisits(visits.filter(v => v.id !== visitId));
    triggerBrainRefresh();
  }, 'Visit deleted', 'Failed to delete visit');

  const handleDeleteVisit = async (visitId) => {
    await executeDeleteVisit(visitId);
  };

  const handleOpenDeal = (dealId) => {
    toast.info('Deal details view - Coming soon!');
    // Navigate to deal details page or open dialog
  };

  const [executeUpdateDealOld, updatingDealOld] = useAsyncAction(async () => {
    if (!selectedDeal) return;
    const response = await leadActivitiesAPI.updateDeal(selectedDeal.id, {
      lead_id: id,
      title: dealForm.title,
      amount: dealForm.amount,
      expected_close_date: dealForm.expectedCloseDate,
      stage: dealForm.stage,
      description: dealForm.description
    });
    setDeals(deals.map(d => d.id === selectedDeal.id ? response.data : d));
    setEditDealDialogOpen(false);
    setSelectedDeal(null);
    triggerBrainRefresh();
  }, 'Deal updated successfully', 'Failed to update deal');

  const handleUpdateDealOld = async () => {
    if (!selectedDeal) return;
    await executeUpdateDealOld();
  };

  const handleUpdateDeal = async () => {
    if (!selectedDeal) return;
    await executeUpdateDealOld();
  };

  const [executeDealStatusChange, changingDealStatus] = useAsyncAction(async (dealId, stage) => {
    await leadActivitiesAPI.updateDealStatus(dealId, stage);
    setDeals(deals.map(d => d.id === dealId ? { ...d, stage } : d));
    triggerBrainRefresh();
  }, 'Deal status updated', 'Failed to update deal status');

  const handleDealStatusChange = async (dealId, stage) => {
    await executeDealStatusChange(dealId, stage);
  };

  const [executeDeleteDeal, deletingDeal] = useAsyncAction(async (dealId) => {
    await leadActivitiesAPI.deleteDeal(dealId);
    setDeals(deals.filter(d => d.id !== dealId));
    triggerBrainRefresh();
  }, 'Deal deleted', 'Failed to delete deal');

  const handleDeleteDeal = async (dealId) => {
    if (!confirm('Are you sure you want to delete this deal?')) return;
    await executeDeleteDeal(dealId);
  };

  const [executeDeleteNote, deletingNote] = useAsyncAction(async (noteId) => {
    await leadActivitiesAPI.deleteNote(noteId);
    setNotes(notes.filter(note => note.id !== noteId));
    triggerBrainRefresh();
  }, 'Note deleted', 'Failed to delete note');

  const handleDeleteNote = async (noteId) => {
    await executeDeleteNote(noteId);
  };

  const [executeDeleteTask, deletingTask] = useAsyncAction(async (taskId) => {
    await leadActivitiesAPI.deleteTask(taskId);
    setTasks(tasks.filter(task => task.id !== taskId));
    triggerBrainRefresh();
  }, 'Task deleted', 'Failed to delete task');

  const handleDeleteTask = async (taskId) => {
    await executeDeleteTask(taskId);
  };

  const [executeUpdateLead, updatingLead] = useAsyncAction(async (updates) => {
    try {
      await leadsAPI.update(id, updates);
      setLead({ ...lead, ...updates });
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(error.response.data?.detail || 'A lead with this email or phone number already exists.');
        throw error;
      }
      throw error;
    }
  }, 'Lead updated', 'Failed to update lead');

  const handleUpdateLead = async (updates) => {
    await executeUpdateLead(updates);
  };

  // Merge manual calls + brain calls, deduplicate by matching timestamps.
  // useMemo (hooks) must run before any early return below (rules of hooks).
  const mergedCalls = useMemo(() => {
    const brainByTime = new Map();
    brainCalls.forEach(c => {
      const ts = (c.created_at || '').substring(0, 19);
      if (ts) brainByTime.set(ts, { ...c, source: 'brain_call' });
    });

    const merged = new Map();
    brainCalls.forEach(c => merged.set(c.id, { ...c, source: 'brain_call' }));
    calls.forEach(c => {
      const ts = (c.created_at || c.createdAt || '').substring(0, 19);
      if (ts && brainByTime.has(ts)) return;
      merged.set(c.id, c);
    });

    const meetingByCallId = {};
    scheduledMeetings.forEach(m => { if (m.call_id) meetingByCallId[m.call_id] = m; });
    return Array.from(merged.values())
      .map(c => ({ ...c, scheduled_meeting: meetingByCallId[c.id] || null }))
      .sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0));
  }, [brainCalls, calls, scheduledMeetings]);

  const allActivities = useMemo(() => [
    ...notes.map(n => ({ ...n, type: 'Note', icon: FileText })),
    ...tasks.map(t => ({ ...t, type: 'Task', icon: CheckCircle })),
    ...followups.map(f => ({ ...f, type: 'Follow-up', icon: Calendar })),
    ...emails.map(e => ({ ...e, type: 'Email', icon: Mail })),
    ...visits.map(v => ({ ...v, type: 'Visit', icon: MapPin })),
    ...mergedCalls.map(c => ({ ...c, type: (c.is_ai_call || c.call_type === 'ai_outbound' || c.source === 'brain_call') ? 'AI Call' : 'Call', icon: (c.is_ai_call || c.call_type === 'ai_outbound' || c.source === 'brain_call') ? Bot : Phone })),
    ...meetings.map(m => ({ ...m, type: 'Meeting', icon: Video })),
    ...whatsappMessages.map(w => ({ ...w, type: 'WhatsApp', icon: MessageCircle, text: w.message }))
  ].sort((a, b) => new Date(b.createdAt || b.created_at || b.sentAt || b.calledAt) - new Date(a.createdAt || a.created_at || a.sentAt || a.calledAt)),
  [notes, tasks, followups, emails, visits, mergedCalls, meetings, whatsappMessages]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="lead-details-loading">
        <div className="h-8 w-64 bg-muted animate-pulse rounded"></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="h-96 bg-muted animate-pulse rounded"></div>
          <div className="col-span-2 h-96 bg-muted animate-pulse rounded"></div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="space-y-4" data-testid="lead-not-found">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Leads
          </Button>
        </div>
        <Card className="p-8 text-center">
          <CardContent>
            <h2 className="text-2xl font-bold mb-2">Lead Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The lead you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Button onClick={() => navigate('/leads')}>
              Go Back to Leads
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleLaunchCommercial = async () => {
    if (!lead?.id || launchingCommercial) return;
    setLaunchingCommercial(true);
    toast.loading('Launching commercial cadence…', { id: 'launch-commercial' });
    try {
      const res = await leadsAPI.launchCommercialCadence(lead.id);
      toast.success(res.data?.message || 'Commercial cadence launched', { id: 'launch-commercial' });
      setLead((prev) => (prev ? { ...prev, commercial_cadence_launched: true } : prev));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not launch commercial cadence', { id: 'launch-commercial' });
    } finally {
      setLaunchingCommercial(false);
    }
  };

  return (
    <div className="h-[calc(100vh-140px)] flex -mx-4 lg:-mx-6 -my-4 lg:-my-6" data-testid="lead-details-page">
      {/* ===== LEFT COLUMN - Contact Profile (Extracted) ===== */}
      <LeadProfileSidebar
        lead={lead}
        currency={currency}
        brainCalls={brainCalls}
        onBack={() => navigate('/leads')}
        onEdit={handleEditLead}
        onDelete={handleDeleteLead}
        onQuickNote={handleQuickNote}
        onQuickEmail={handleQuickEmail}
        onQuickCall={handleQuickCall}
        onQuickTask={() => setTaskDialogOpen(true)}
        onQuickMeeting={handleQuickMeeting}
        onAICallClick={() => { setActiveTab('intelligence'); setIntelligenceSubTab('brain-calls'); }}
        onRevealContact={handleRevealContact}
        revealing={revealing}
        getUserName={getUserName}
      />

      {/* Column Separator */}
      <div className="w-px bg-border shrink-0" />

      {/* ===== CENTER COLUMN - Main Content ===== */}
      <div className="flex-1 min-w-0 flex flex-col" data-testid="lead-center-column">
        {/* Phase 2 (b) — commercial cadence ready-to-launch banner */}
        {lead?.commercial_cadence_proposed && !lead?.commercial_cadence_launched && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800"
            data-testid="commercial-cadence-banner"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Rocket className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <p className="text-sm text-emerald-900 dark:text-emerald-200 truncate">
                <span className="font-semibold">Commercial cadence ready.</span>{' '}
                This lead is buying-ready — launch the closing sequence. Nothing sends until you approve.
              </p>
            </div>
            <button
              onClick={handleLaunchCommercial}
              disabled={launchingCommercial}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-1.5 transition-colors"
              data-testid="launch-commercial-cadence-btn"
            >
              {launchingCommercial ? 'Launching…' : 'Launch cadence'}
            </button>
          </div>
        )}
        {/* Parent Tabs Header - Aligned with left column header */}
        <div className="flex items-center h-12 border-b border-border shrink-0">
          <button
            onClick={() => setActiveTab('journey')}
            className={`px-6 py-3 text-sm font-medium border-r border-border whitespace-nowrap transition-colors flex items-center gap-1.5 ${activeTab === 'journey' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
            data-testid="tab-journey"
          >
            <Route className="w-3.5 h-3.5 text-emerald-500" />
            Journey
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-6 py-3 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activeTab === 'activity' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
            data-testid="tab-activity"
          >
            Activity ({allActivities.length})
          </button>
          <button
            onClick={() => setActiveTab('intelligence')}
            className={`px-6 py-3 text-sm font-medium border-r border-border whitespace-nowrap transition-colors flex items-center gap-1.5 ${activeTab === 'intelligence' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
            data-testid="tab-intelligence"
          >
            <svg className="w-3.5 h-3.5 text-violet-500" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M13.6027 1.12267C13.0575 -0.373899 10.9421 -0.373899 10.397 1.12267L7.92324 7.92324L1.12267 10.397C-0.373899 10.9421 -0.373899 13.0575 1.12267 13.6027L7.92324 16.0764L10.397 22.877C10.9421 24.3735 13.0575 24.3735 13.6027 22.877L16.0764 16.0764L22.877 13.6027C24.3735 13.0575 24.3735 10.9421 22.877 10.397L16.0764 7.92324L13.6027 1.12267Z" />
            </svg>
            Intelligence
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-muted/30">
          <Tabs value={activeTab} onValueChange={setActiveTab}>

            {/* Journey Tab — unified cross-channel timeline */}
            <TabsContent value="journey" className="mt-0">
              <LeadJourneyTimeline leadId={id} />
            </TabsContent>

            {/* Activity Tab (formerly Activities - now the primary tab) */}
            <TabsContent value="activity" className="space-y-4 mt-0">
              <Tabs value={activityTab} onValueChange={setActivityTab}>
                {/* Child tabs - scrollable row */}
                <div className="flex items-center border border-border rounded-md bg-muted/30 mb-3 overflow-x-auto scrollbar-thin scrollbar-thumb-border" data-testid="activity-subtab-bar">
                  <button
                    onClick={() => setActivityTab('all')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'all' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    All ({allActivities.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('notes')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'notes' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Notes ({notes.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('tasks')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'tasks' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Tasks ({tasks.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('followups')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'followups' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Follow-ups ({followups.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('emails')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'emails' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Emails ({emails.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('calls')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'calls' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Calls ({mergedCalls.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('meetings')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'meetings' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Meetings ({meetings.length + scheduledMeetings.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('deals')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'deals' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Deals ({deals.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('visits')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'visits' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  >
                    Visits ({visits.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('docs')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${activityTab === 'docs' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                    data-testid="doc-tracker-tab"
                  >
                    Docs ({attachments.length})
                  </button>
                  <button
                    onClick={() => setActivityTab('whatsapp')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium whitespace-nowrap transition-colors ${activityTab === 'whatsapp' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                    data-testid="tab-whatsapp"
                  >
                    WhatsApp ({Math.max(waCount, whatsappMessages.length)})
                  </button>
                  <button
                    onClick={() => setActivityTab('linkedin')}
                    className={`shrink-0 px-3 py-3.5 text-sm font-medium whitespace-nowrap transition-colors ${activityTab === 'linkedin' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                    data-testid="tab-linkedin"
                  >
                    LinkedIn ({Math.max(liCount, linkedinMessages.length)})
                  </button>
                </div>
                
                {/* Activity Sub-tabs Content - Extracted to LeadActivityContent */}
                <LeadActivityContent
                  lead={lead}
                  notes={notes}
                  tasks={tasks}
                  followups={followups}
                  emails={emails}
                  calls={calls}
                  meetings={meetings}
                  deals={deals}
                  visits={visits}
                  activityTab={activityTab}
                  commentText={commentText}
                  docVerification={docVerification}
                  docFollowupLoading={docFollowupLoading}
                  docLoading={docLoading}
                  setActivityTab={setActivityTab}
                  setNoteDialogOpen={setNoteDialogOpen}
                  setTaskDialogOpen={setTaskDialogOpen}
                  setFollowupDialogOpen={setFollowupDialogOpen}
                  setEmailDialogOpen={setEmailDialogOpen}
                  setCallDialogOpen={setCallDialogOpen}
                  setMeetingDialogOpen={setMeetingDialogOpen}
                  setVisitDialogOpen={setVisitDialogOpen}
                  setSendWhatsAppOpen={setSendWhatsAppOpen}
                  setDocLoading={setDocLoading}
                  setDocFollowupLoading={setDocFollowupLoading}
                  setDocVerification={setDocVerification}
                  setCommentText={setCommentText}
                  handleDeleteNote={handleDeleteNote}
                  handleAddComment={handleAddComment}
                  handleToggleTask={handleToggleTask}
                  handleTaskStatusChange={handleTaskStatusChange}
                  handleDeleteTask={handleDeleteTask}
                  handleFollowUpStatusChange={handleFollowUpStatusChange}
                  handleRescheduleFollowUp={handleRescheduleFollowUp}
                  handleDeleteFollowUp={handleDeleteFollowUp}
                  handleAddFollowUpSummary={handleAddFollowUpSummary}
                  handleDeleteEmail={handleDeleteEmail}
                  handleCreateDeal={handleCreateDeal}
                  handleViewDeal={handleViewDeal}
                  formatLocal={formatLocal}
                  formatLocalDate={formatLocalDate}
                  formatLocalTime={formatLocalTime}
                  API_URL={process.env.REACT_APP_BACKEND_URL}
                  token={localStorage.getItem('access_token')}
                  noteComments={noteComments}
                  getCreatorName={getCreatorName}
                  allActivities={allActivities}
                  id={id}
                  mergedCalls={mergedCalls}
                  scheduledMeetings={scheduledMeetings}
                  highlightedCallId={highlightedCallId}
                />

              </Tabs>
            </TabsContent>

          {/* Intelligence Tab - with sub-tabs */}
          <TabsContent value="intelligence" className="mt-0 space-y-0">
            {/* Intelligence Sub-tabs */}
            <div className="flex items-center border border-border rounded-md bg-muted/30 mb-4 overflow-hidden">
              <button
                onClick={() => setIntelligenceSubTab('overview')}
                className={`flex-1 px-3 py-3 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${intelligenceSubTab === 'overview' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                data-testid="intel-subtab-overview"
              >
                AI Analysis
              </button>
              <button
                onClick={() => setIntelligenceSubTab('cadence')}
                className={`flex-1 px-3 py-3 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${intelligenceSubTab === 'cadence' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                data-testid="intel-subtab-cadence"
              >
                Smart Cadence
              </button>
              <button
                onClick={() => setIntelligenceSubTab('linkedin')}
                className={`flex-1 px-3 py-3 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${intelligenceSubTab === 'linkedin' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                data-testid="intel-subtab-linkedin"
              >
                LinkedIn
              </button>
              <button
                onClick={() => setIntelligenceSubTab('brain-calls')}
                className={`flex-1 px-3 py-3 text-sm font-medium border-r border-border whitespace-nowrap transition-colors ${intelligenceSubTab === 'brain-calls' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                data-testid="intel-subtab-brain-calls"
              >
                AI Brain Calls
              </button>
              <button
                onClick={() => setIntelligenceSubTab('memory')}
                className={`flex-1 px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors ${intelligenceSubTab === 'memory' ? 'bg-card text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                data-testid="intel-subtab-memory"
              >
                Brain Memory
              </button>
            </div>

            {/* Quick Insights Card - Real-time Health Score */}
            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="quick-insights-card">
              {/* AI Score */}
              <div className="relative overflow-hidden rounded-lg border border-border bg-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">AI Score</span>
                  <div className={`h-2 w-2 rounded-full animate-pulse ${
                    (lead.ai_score || 0) >= 70 ? 'bg-red-500' : (lead.ai_score || 0) >= 40 ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold tracking-tight">{lead.ai_score ?? '—'}</span>
                  <span className="text-xs text-muted-foreground mb-1">/100</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      (lead.ai_score || 0) >= 70 ? 'bg-red-500' : (lead.ai_score || 0) >= 40 ? 'bg-amber-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${lead.ai_score || 0}%` }}
                  />
                </div>
                <span className={`mt-1.5 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  lead.ai_qualification === 'hot' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' :
                  lead.ai_qualification === 'warm' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' :
                  lead.ai_qualification === 'cold' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {lead.ai_qualification?.toUpperCase() || 'UNSCORED'}
                </span>
              </div>

              {/* Engagement Level */}
              <div className="relative overflow-hidden rounded-lg border border-border bg-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Engagement</span>
                  <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold tracking-tight">{allActivities.length}</span>
                  <span className="text-xs text-muted-foreground mb-1">activities</span>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  {(() => {
                    const engagement = lead.ai_engagement || (allActivities.length > 10 ? 'positive' : allActivities.length > 3 ? 'neutral' : 'low');
                    const configs = {
                      positive: { label: 'High', cls: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500' },
                      neutral: { label: 'Medium', cls: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500' },
                      negative: { label: 'Low', cls: 'text-red-600 dark:text-red-400', bg: 'bg-red-500' },
                      low: { label: 'Low', cls: 'text-red-600 dark:text-red-400', bg: 'bg-red-500' },
                    };
                    const cfg = configs[engagement] || configs.neutral;
                    return (
                      <>
                        <div className={`h-2 w-2 rounded-full ${cfg.bg}`} />
                        <span className={`text-xs font-medium ${cfg.cls}`}>{cfg.label} Engagement</span>
                      </>
                    );
                  })()}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  {lead.ai_engagement_signals ? lead.ai_engagement_signals.substring(0, 60) + '...' : `${allActivities.filter(a => a.type === 'AI Call').length} AI calls, ${allActivities.filter(a => a.type === 'Email').length} emails`}
                </p>
              </div>

              {/* ICP Fit */}
              <div className="relative overflow-hidden rounded-lg border border-border bg-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">ICP Fit</span>
                  <Target className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold tracking-tight">{lead.ai_icp_match?.score ?? '—'}</span>
                  <span className="text-xs text-muted-foreground mb-1">/100</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      (lead.ai_icp_match?.score || 0) >= 70 ? 'bg-emerald-500' : (lead.ai_icp_match?.score || 0) >= 40 ? 'bg-amber-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${lead.ai_icp_match?.score || 0}%` }}
                  />
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(lead.ai_icp_match?.matching_factors || []).slice(0, 2).map((f, i) => (
                    <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/30 truncate max-w-[120px]">{f}</span>
                  ))}
                  {(lead.ai_risk_flags || []).length > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200/50 dark:border-red-800/30">
                      {lead.ai_risk_flags.length} risk{lead.ai_risk_flags.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {intelligenceSubTab === 'overview' && (
              <div className="space-y-5">
                {/* AI Brain Analysis */}
                {lead.ai_score != null && (
                  <div className="border border-border rounded-lg overflow-hidden" data-testid="lead-ai-intelligence">
                    <div className="px-5 py-3.5 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-950/40 dark:to-indigo-950/40 border-b border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M13.6027 1.12267C13.0575 -0.373899 10.9421 -0.373899 10.397 1.12267L7.92324 7.92324L1.12267 10.397C-0.373899 10.9421 -0.373899 13.0575 1.12267 13.6027L7.92324 16.0764L10.397 22.877C10.9421 24.3735 13.0575 24.3735 13.6027 22.877L16.0764 16.0764L22.877 13.6027C24.3735 13.0575 24.3735 10.9421 22.877 10.397L16.0764 7.92324L13.6027 1.12267Z" />
                          </svg>
                          AI Brain Analysis
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-[10px] text-violet-600 hover:text-violet-700 hover:bg-violet-100 dark:text-violet-400 dark:hover:text-violet-300 dark:hover:bg-violet-900/40"
                            disabled={reanalyzing}
                            onClick={handleReanalyze}
                            data-testid="reanalyze-lead-btn"
                          >
                            {reanalyzing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                            {reanalyzing ? 'Analyzing...' : 'Re-analyze'}
                          </Button>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            lead.ai_qualification === 'hot' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400' :
                            lead.ai_qualification === 'warm' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' :
                            lead.ai_qualification === 'cold' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' :
                            'bg-muted text-muted-foreground'
                          }`}>{lead.ai_qualification?.toUpperCase()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="px-5 py-4 space-y-4">
                      {/* Score Bar + Summary */}
                      <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center gap-1 shrink-0">
                          <div className="relative w-14 h-14">
                            <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                              <circle cx="18" cy="18" r="15.5" fill="none" className="stroke-border" strokeWidth="2" />
                              <circle cx="18" cy="18" r="15.5" fill="none"
                                stroke={lead.ai_score >= 70 ? '#ef4444' : lead.ai_score >= 40 ? '#f59e0b' : '#3b82f6'}
                                strokeWidth="2.5" strokeDasharray={`${lead.ai_score} ${100 - lead.ai_score}`} strokeLinecap="round" />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">{lead.ai_score}</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground">Score</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Summary</p>
                          <div className="space-y-1.5">
                            {(lead.ai_reasoning || 'AI analysis pending...').split(/[.!]\s+|—\s*/).filter(s => s.trim().length > 10).slice(0, 4).map((sentence, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <div className="w-1 h-1 rounded-full bg-violet-400 mt-1.5 shrink-0" />
                                <p className="text-xs text-foreground/80 leading-relaxed">{sentence.trim().replace(/\.$/, '')}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* ICP Match Factors */}
                      {lead.ai_icp_match && lead.ai_icp_match.matching_factors && lead.ai_icp_match.matching_factors.length > 0 && (
                        <div className="rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/50 dark:border-emerald-800/30 p-3">
                          <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <CheckCircle className="h-3 w-3" />
                            ICP Match Factors
                          </p>
                          <div className="space-y-1">
                            {lead.ai_icp_match.matching_factors.map((f, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <div className="w-1 h-1 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                                <p className="text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">{f}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risk Flags */}
                      {lead.ai_risk_flags && lead.ai_risk_flags.length > 0 && (
                        <div className="rounded-lg bg-red-50/50 dark:bg-red-950/20 border border-red-200/50 dark:border-red-800/30 p-3">
                          <p className="text-[11px] font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <Activity className="h-3 w-3" />
                            Risk Flags
                          </p>
                          <div className="space-y-1">
                            {lead.ai_risk_flags.map((f, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <div className="w-1 h-1 rounded-full bg-red-500 mt-1.5 shrink-0" />
                                <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">{f}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {lead.ai_last_analyzed && (
                        <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">Last analyzed: {formatLocal(lead.ai_last_analyzed)}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Brain Actions — AI-authored actions (e.g. commercial cadence proposals) */}
                {aiActivities && aiActivities.length > 0 && (
                  <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="brain-actions-panel">
                    <div className="flex items-center gap-2">
                      <Rocket className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      <h4 className="text-sm font-semibold">Brain Actions</h4>
                    </div>
                    <div className="space-y-2">
                      {aiActivities.slice(0, 6).map((a, i) => (
                        <div key={a.id || i} className="text-sm border-l-2 border-emerald-500/60 pl-3 py-1" data-testid="brain-action-item">
                          <p className="text-foreground">{a.summary || 'AI action'}</p>
                          {a.created_at && (
                            <p className="text-[10px] text-muted-foreground pt-0.5">{formatLocal(a.created_at)}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Brain Activity Timeline */}
                <BrainActivityTimeline leadId={id} />

                {/* Lead Intelligence Panel */}
                <LeadIntelligencePanel
                  leadId={lead.id}
                  leadName={lead.name}
                  onAction={(action, label) => {
                    if (action === 'send_email' || action === 'follow_up') {
                      setAiDraftAction({ type: action, label: label || action });
                      setAiDraftEmailOpen(true);
                    }
                    else if (action === 'schedule_demo') {
                      setAiDraftAction({ type: action, label: label || 'Schedule demo' });
                      setAiDraftEmailOpen(true);
                    }
                    else if (action === 'call') setCallDialogOpen(true);
                    else if (action === 'create_task' || action === 'qualify') setTaskDialogOpen(true);
                  }}
                />

                {/* Closing Strategy */}
                <ClosingStrategyPanel leadId={id} leadStatus={lead?.status} />

                {/* Re-engagement Queue */}
                <ReengagementQueue leadId={id} refreshKey={brainRefreshKey} />
              </div>
            )}

            {/* Smart Cadence Sub-tab */}
            {intelligenceSubTab === 'cadence' && (
              <SmartCadencePanel
                leadId={id}
                leadName={lead?.name}
                leadNeedsReview={!!lead?.needs_review}
                refreshKey={brainRefreshKey}
                onApproved={loadLead}
              />
            )}

            {/* LinkedIn Outreach Sub-tab */}
            {intelligenceSubTab === 'linkedin' && (
              <LinkedInOutreachPanel leadId={id} leadName={lead?.name} linkedinUrl={lead?.linkedin_url} />
            )}

            {/* AI Brain Calls Sub-tab */}
            {intelligenceSubTab === 'brain-calls' && (
              <BrainCallPanel leadId={id} leadName={lead?.name} leadPhone={lead?.phone} refreshKey={brainRefreshKey} />
            )}

            {/* Brain Memory Sub-tab */}
            {intelligenceSubTab === 'memory' && (
              <BrainMemoryPanel leadId={id} leadName={lead?.name} refreshKey={brainRefreshKey} />
            )}
          </TabsContent>

          </Tabs>
        </div>
      </div>

      {/* Note Off-Canvas */}
      <AddNoteSheet
        isOpen={noteDialogOpen}
        onClose={setNoteDialogOpen}
        onSave={handleAddNote}
        saving={addingNote}
      />

      <VisitDialog open={visitDialogOpen} onOpenChange={setVisitDialogOpen} form={visitForm} setForm={setVisitForm} onSubmit={handleAddVisit} />
      <MeetingSheet open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen} form={meetingForm} setForm={setMeetingForm} onSubmit={handleAddMeeting}
        leadId={id} bookingLink={bookingLink} loadingBookingLink={loadingBookingLink}
        onGetBookingLink={handleGetBookingLink} onCopyLink={handleCopyBookingLink}
        onInsertEmail={handleInsertBookingIntoEmail} onSendWhatsApp={handleSendBookingWhatsApp} />
      <MakeCallDialog open={makeCallDialogOpen} onOpenChange={setMakeCallDialogOpen} form={callForm} setForm={setCallForm} onSubmit={handleSaveCall} />
      <CallSummaryDialog open={callSummaryDialogOpen} onOpenChange={setCallSummaryDialogOpen} form={callForm} setForm={setCallForm} onSubmit={handleSaveCall} />
      <EditDealDialog open={editDealDialogOpen} onOpenChange={setEditDealDialogOpen} form={dealForm} setForm={setDealForm} onSubmit={handleUpdateDeal} />

      {/* New Components */}
      <EditLeadOffcanvas
        isOpen={editLeadOpen}
        onClose={() => setEditLeadOpen(false)}
        lead={lead}
        onSuccess={() => {
          loadLead();
          setEditLeadOpen(false);
        }}
      />

      <CreateDealOffcanvas
        isOpen={createDealOpen}
        onClose={() => setCreateDealOpen(false)}
        leadId={id}
        leadName={lead?.name}
        onSuccess={handleDealSuccess}
      />

      <ViewDealOffcanvas
        isOpen={viewDealOpen}
        onClose={() => setViewDealOpen(false)}
        deal={selectedDeal}
        onEdit={handleEditDeal}
        onDelete={handleDealDeleted}
        onConvertToQuote={handleDealConvertedToQuote}
      />

      <EditDealOffcanvas
        isOpen={editDealOpen}
        onClose={() => setEditDealOpen(false)}
        deal={selectedDeal}
        onSuccess={handleDealSuccess}
      />

      <SendEmailModal
        isOpen={sendEmailOpen}
        onClose={() => setSendEmailOpen(false)}
        leadId={id}
        leadEmail={lead?.email}
        leadName={lead?.name}
      />

      <SendWhatsAppModal
        isOpen={sendWhatsAppOpen}
        onClose={() => setSendWhatsAppOpen(false)}
        leadId={id}
        leadPhone={lead?.phone}
        leadName={lead?.name}
      />

      <AIDraftEmailSheet
        isOpen={aiDraftEmailOpen}
        onClose={() => setAiDraftEmailOpen(false)}
        leadId={id}
        leadEmail={lead?.email}
        leadName={lead?.name}
        actionType={aiDraftAction.type}
        actionLabel={aiDraftAction.label}
        onSent={() => loadActivities()}
      />

      <TaskSheet open={taskDialogOpen} onOpenChange={setTaskDialogOpen} form={taskForm} setForm={setTaskForm} onSubmit={handleCreateTask} />
      <FollowupSheet open={followupDialogOpen} onOpenChange={setFollowupDialogOpen} form={followupForm} setForm={setFollowupForm} onSubmit={handleCreateFollowup} />
      <EmailSheet open={emailDialogOpen} onOpenChange={setEmailDialogOpen} form={emailForm} setForm={setEmailForm} onSubmit={handleCreateEmail} leadEmail={lead?.email} />
      <CallSheet open={callDialogOpen} onOpenChange={setCallDialogOpen} form={callForm} setForm={setCallForm} onSubmit={handleCreateCall} />

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule Follow-up</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reschedule-date" className="text-sm font-medium">New Date</Label>
            <Input
              id="reschedule-date"
              type="date"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="mt-2"
              data-testid="reschedule-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)} data-testid="reschedule-cancel-btn">Cancel</Button>
            <Button onClick={confirmReschedule} disabled={!rescheduleDate} data-testid="reschedule-confirm-btn">
              <Calendar className="h-4 w-4 mr-2" />
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
